import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import SmartImage from "@/components/ui/SmartImage";
import { cn } from "@/lib/utils";

interface RecapStoryStripProps {
  articleIds?: string[] | null;
  /** Thumbnail size in px. */
  size?: number;
  className?: string;
}

/**
 * Small horizontal strip of cover images for the stories a daily recap covers.
 * Uses the mirrored image copies, so it only pulls tiny renditions.
 */
export const useRecapStories = (articleIds?: string[] | null) => {
  const ids = (articleIds ?? []).slice(0, 12);

  const { data } = useQuery({
    queryKey: ["recap-story-strip", ids],
    enabled: ids.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("id, slug, title, image_url")
        .in("id", ids);
      if (error) throw error;
      return (data ?? []).filter((a) => a.image_url);
    },
  });

  return data ?? [];
};

/**
 * Square artwork that auto-cycles through the covers of the stories in a recap.
 */
export const RecapCoverCycler = ({
  articleIds,
  className,
  intervalMs = 2500,
  imageSize = 96,
}: {
  articleIds?: string[] | null;
  className?: string;
  intervalMs?: number;
  imageSize?: number;
}) => {
  const stories = useRecapStories(articleIds);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (stories.length < 2) return;
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % stories.length),
      intervalMs
    );
    return () => window.clearInterval(id);
  }, [stories.length, intervalMs]);

  if (stories.length === 0) return null;

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {stories.map((story, i) => (
        <SmartImage
          key={story.id}
          src={story.image_url}
          w={imageSize}
          h={imageSize}
          alt={story.title}
          showPlaceholder={false}
          noOriginalFallback={imageSize <= 160}
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-700",
            i === index % stories.length ? "opacity-100" : "opacity-0"
          )}
        />
      ))}
    </div>
  );
};

const RecapStoryStrip = ({ articleIds, size = 44, className }: RecapStoryStripProps) => {
  const stories = useRecapStories(articleIds);

  if (stories.length === 0) return null;

  return (
    <div
      className={cn("flex gap-1.5 overflow-x-auto scrollbar-hide -mx-0.5 px-0.5 py-0.5", className)}
    >
      {stories.map((story) => (
        <Link
          key={story.id}
          to={`/article/${story.slug}`}
          title={story.title}
          className="shrink-0 rounded-md overflow-hidden ring-1 ring-border/50 hover:ring-primary/60 transition"
          style={{ width: size, height: size }}
        >
          <SmartImage
            src={story.image_url}
            w={size * 2}
            h={size * 2}
            alt={story.title}
            showPlaceholder
            noOriginalFallback
            className="h-full w-full object-cover"
          />
        </Link>
      ))}
    </div>
  );
};

export default RecapStoryStrip;
